import { NavLink, useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, 
  CheckCircle, 
  Activity, 
  Bell, 
  FileText, 
  Settings, 
  ScrollText,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  FileCheck,
  Users,
  GitBranch,
  ClipboardCheck,
  Target,
  LifeBuoy,
  Building2,
  TrendingUp,
  Crosshair,
  ShieldCheck,
  Gauge,
  LayoutDashboard,
  Bot,
  Plug,
  Server,
  Brain,
  Webhook,
  Key
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useState, useEffect } from 'react';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavigationSection {
  id: string;
  title: string;
  icon: any;
  color: string; // Tailwind color class
  bgColor: string; // Background color class
  items: NavigationItem[];
}

interface NavigationItem {
  name: string;
  path: string;
  icon: any;
  count?: number;
  submenu?: SubMenuItem[];
}

interface SubMenuItem {
  name: string;
  path: string;
  icon: any;
}

// Color-coded navigation sections
const navigationSections: NavigationSection[] = [
  {
    id: 'capture',
    title: 'Capture',
    icon: Crosshair,
    color: 'text-cyan-300',
    bgColor: 'bg-cyan-500/20',
    items: [
      {
        name: 'Your Business',
        path: '/business',
        icon: Building2,
        submenu: [
          {
            name: 'Business Processes',
            path: '/processes',
            icon: GitBranch,
          },
          {
            name: 'Capacity & Skills',
            path: '/capacity-skills',
            icon: Users,
          },
          {
            name: 'Suppliers & Systems',
            path: '/suppliers',
            icon: Server,
          },
          {
            name: 'Customers',
            path: '/customers',
            icon: Building2,
          },
          {
            name: 'Business Forecast',
            path: '/business-forecast',
            icon: TrendingUp,
          },
        ],
      },
      {
        name: 'Risk Register',
        path: '/risks',
        icon: Shield,
      },
    ],
  },
  {
    id: 'define',
    title: 'Define Appetite',
    icon: Target,
    color: 'text-purple-300',
    bgColor: 'bg-purple-500/20',
    items: [
      {
        name: 'Policies',
        path: '/policies',
        icon: FileCheck,
      },
      {
        name: 'Risk Appetite',
        path: '/risk-appetite',
        icon: Target,
      },
    ],
  },
  {
    id: 'assess',
    title: 'Assess',
    icon: ClipboardCheck,
    color: 'text-blue-300',
    bgColor: 'bg-blue-500/20',
    items: [
      {
        name: 'Skills Intelligence',
        path: '/capacity-skills/intelligence',
        icon: Brain,
      },
      {
        name: 'Scenarios',
        path: '/scenarios',
        icon: GitBranch,
      },
      {
        name: 'Themis',
        path: '/integrations',
        icon: Bot,
      },
      {
        name: 'Assessments',
        path: '/assessments',
        icon: ClipboardCheck,
      },
    ],
  },
  {
    id: 'mitigate',
    title: 'Mitigate',
    icon: ShieldCheck,
    color: 'text-green-300',
    bgColor: 'bg-green-500/20',
    items: [
      {
        name: 'Controls',
        path: '/controls',
        icon: CheckCircle,
      },
      {
        name: 'Compliance',
        path: '/compliance',
        icon: Activity,
      },
      {
        name: 'Risk Actions',
        path: '/actions',
        icon: Target,
      },
      {
        name: 'Scenario Playbooks',
        path: '/playbooks',
        icon: GitBranch,
      },
      {
        name: 'BCP Plan',
        path: '/bcp',
        icon: LifeBuoy,
      },
      {
        name: 'Real-time KPIs',
        path: '/kpis',
        icon: Gauge,
      },
      {
        name: 'Alert Configuration',
        path: '/alerts',
        icon: Bell,
      },
    ],
  },
  {
    id: 'admin',
    title: 'Administration',
    icon: Settings,
    color: 'text-neutral-300',
    bgColor: 'bg-neutral-500/20',
    items: [
      {
        name: 'Notifications',
        path: '/notifications',
        icon: Bell,
      },
      {
        name: 'Reports',
        path: '/reports',
        icon: FileText,
      },
      {
        name: 'Audit Trail',
        path: '/audit',
        icon: ScrollText,
      },
      {
        name: 'Integrations',
        path: '/admin/integrations',
        icon: Plug,
        submenu: [
          {
            name: 'All Integrations',
            path: '/admin/integrations',
            icon: Plug,
          },
          {
            name: 'MCP Server',
            path: '/admin/integrations/mcp',
            icon: Server,
          },
          {
            name: 'AI Monitoring',
            path: '/admin/integrations/ai',
            icon: Brain,
          },
          {
            name: 'APIs & Webhooks',
            path: '/admin/integrations/apis',
            icon: Webhook,
          },
          {
            name: 'Authentication',
            path: '/admin/integrations/auth',
            icon: Key,
          },
        ],
      },
      {
        name: 'Settings',
        path: '/settings',
        icon: Settings,
      },
    ],
  },
];

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<string>('capture');
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  // Determine active section based on current path
  useEffect(() => {
    const currentPath = location.pathname;
    
    // Build a flat list of all items with their section id, sorted by path length (longest first)
    const allItems = navigationSections.flatMap(section => 
      section.items.map(item => ({ sectionId: section.id, path: item.path }))
    ).sort((a, b) => b.path.length - a.path.length);
    
    // Find the first matching item (most specific path wins)
    const match = allItems.find(item => currentPath.startsWith(item.path));
    
    if (match) {
      setActiveSection(match.sectionId);
      return;
    }
    
    // Default to dashboard if no match (or keep current)
    if (currentPath === '/dashboard') {
      setActiveSection('capture'); // Default section for dashboard
    }
  }, [location.pathname]);

  const currentSection = navigationSections.find(s => s.id === activeSection) ?? navigationSections[0]!;

  // Handle Command/Ctrl + Click on logo to access DevTools
  const handleLogoClick = (e: React.MouseEvent) => {
    if (e.metaKey || e.ctrlKey) {
      e.preventDefault();
      navigate('/devtools');
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed top-0 left-0 z-50 h-screen w-64 border-r border-neutral-800 transition-all duration-300 ease-in-out lg:translate-x-0 lg:static lg:z-40",
          isOpen ? "translate-x-0" : "-translate-x-full",
          currentSection ? `${currentSection.bgColor} bg-opacity-5` : "bg-neutral-950"
        )}
        style={{
          backgroundColor: currentSection ? `rgb(from ${currentSection.color} r g b / 0.05)` : undefined,
        }}
      >
        {/* Sidebar Header */}
        <div className={cn(
          "flex items-center justify-between h-16 px-6 border-b",
          currentSection ? `border-current/10 ${currentSection.bgColor}` : "border-neutral-800"
        )}>
          <button 
            onClick={handleLogoClick}
            className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
            title="Cmd/Ctrl + Click for DevTools"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-r from-primary-500 to-cyan-500">
              <span className="text-sm font-bold text-black">L4</span>
            </div>
            <div>
              <span className="text-lg font-semibold text-white">Line4</span>
              <span className="ml-1 text-sm text-neutral-400">.Observability</span>
            </div>
          </button>
          
          {/* Close button for mobile */}
          <button
            onClick={onClose}
            className="lg:hidden p-2 text-neutral-400 hover:text-white transition-colors"
          >
            <ChevronLeft size={20} />
          </button>
        </div>

        {/* Navigation Items - Shows only current section */}
        <nav className="flex-1 px-4 py-6 overflow-y-auto">
          {/* Dashboard Link - Always visible */}
          <div className="mb-6">
            <NavLink
              to="/dashboard"
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200',
                  'hover:bg-primary-500/10',
                  isActive
                    ? 'bg-gradient-to-r from-primary-500 to-cyan-500 text-black border-2 border-primary-400 shadow-lg'
                    : 'text-neutral-200 hover:text-white border border-neutral-800'
                )
              }
            >
              <LayoutDashboard className="w-5 h-5 flex-shrink-0" />
              <span className="flex-1 font-semibold">Dashboard</span>
            </NavLink>
          </div>

          {/* Current Section Items */}
          <div className="space-y-1">
            {currentSection.items.map((item) => (
              <div key={item.path}>
                {/* Main Item */}
                {item.submenu ? (
                  <button
                    onClick={() => {
                      const isExpanded = expandedItems.includes(item.path);
                      setExpandedItems(isExpanded 
                        ? expandedItems.filter(p => p !== item.path)
                        : [...expandedItems, item.path]
                      );
                    }}
                    className={cn(
                      'w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200',
                      `hover:bg-${currentSection.color}/10`,
                      'text-neutral-200 hover:text-white'
                    )}
                  >
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    <span className="flex-1 text-left whitespace-nowrap overflow-hidden text-ellipsis">{item.name}</span>
                    {expandedItems.includes(item.path) ? (
                      <ChevronDown className="w-4 h-4 flex-shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 flex-shrink-0" />
                    )}
                  </button>
                ) : (
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200',
                        `hover:bg-${currentSection.color}/10`,
                        isActive
                          ? `bg-${currentSection.color}/30 text-${currentSection.color} border-2 border-${currentSection.color}/50 shadow-md`
                          : 'text-neutral-200 hover:text-white'
                      )
                    }
                  >
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    <span className="flex-1 whitespace-nowrap overflow-hidden text-ellipsis">{item.name}</span>
                    {item.count !== undefined && (
                      <span className={cn(
                        'text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0',
                        `bg-${currentSection.color}/30 text-${currentSection.color} border border-${currentSection.color}/50`
                      )}>
                        {item.count}
                      </span>
                    )}
                  </NavLink>
                )}

                {/* Submenu Items */}
                {item.submenu && expandedItems.includes(item.path) && (
                  <div className="ml-8 mt-1 space-y-1">
                    {item.submenu.map((subItem) => (
                      <NavLink
                        key={subItem.path}
                        to={subItem.path}
                        className={({ isActive }) =>
                          cn(
                            'flex items-center gap-3 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200',
                            `hover:bg-${currentSection.color}/10`,
                            isActive
                              ? `bg-${currentSection.color}/30 text-${currentSection.color} border-2 border-${currentSection.color}/50 shadow-md`
                              : 'text-neutral-400 hover:text-white'
                          )
                        }
                      >
                        <subItem.icon className="w-4 h-4 flex-shrink-0" />
                        <span className="flex-1 whitespace-nowrap overflow-hidden text-ellipsis text-xs">{subItem.name}</span>
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </nav>

        {/* Sidebar Footer */}
        <div className="border-t border-neutral-800 p-4">
          <p className="text-xs text-neutral-600">Version 2.27.0</p>
        </div>
      </aside>
    </>
  );
}
